import type { ExtendParameters } from '@dynamic-labs-sdk/client/core';
import { toHex } from 'viem';

import type {
  EIP1193Provider,
  EvmWalletProvider,
} from '../../../EvmWalletProvider.types';

// source: https://docs.metamask.io/wallet/reference/json-rpc-methods/wallet_addethereumchain/
type RequestAddChainPayload = {
  /**
   * An array of one or more URLs pointing to block explorer sites for the chain.
   */
  blockExplorerUrls?: string[];
  /**
   * The chain ID as a 0x-prefixed hexadecimal string, per the eth_chainId method.
   * MetaMask compares this chain ID value with the eth_chainId return value.
   * If these values aren't identical, MetaMask rejects the request.
   */
  chainId: string;
  /**
   * A human-readable name for the chain.
   */
  chainName: string;
  /**
   * An array of one or more URLs pointing to icons that can be used to visually identify the chain.
   */
  iconUrls?: string[];
  /**
   * An object containing information about the native currency of the chain.
   */
  nativeCurrency: {
    /**
     * A non-negative integer representing the number of decimals the native currency uses.
     */
    decimals: number;
    /**
     * A human-readable name of the native currency.
     */
    name: string;
    /**
     * A human-readable symbol of the native currency.
     */
    symbol: string;
  };
  /**
   * An array of one or more URLs pointing to RPC endpoints that can be used to communicate with the chain.
   * At least one item is required.
   */
  rpcUrls: string[];
};

type AddNetwork = ExtendParameters<
  EvmWalletProvider['addNetwork'],
  {
    eip1193: EIP1193Provider;
  }
>;

/**
 * Allows requesting that the eip1193 provider adds a network to its list of available networks.
 * @not-instrumented
 */
export const addNetwork: AddNetwork = async ({ eip1193, networkData }) => {
  const chainId = toHex(parseInt(networkData.networkId));

  const payload: RequestAddChainPayload = {
    blockExplorerUrls: networkData.blockExplorerUrls,
    chainId,
    chainName: networkData.displayName,
    iconUrls: [networkData.iconUrl],
    nativeCurrency: networkData.nativeCurrency,
    rpcUrls: networkData.rpcUrls.http,
  };

  await eip1193.request({
    method: 'wallet_addEthereumChain',
    params: [payload],
  });
};
